import React from "react";
import useReducedMotion from "../hooks/useReducedMotion";

// Subtle entrance animation: the hook returns a ref + a `visible` boolean
// that flips to `true` the first time the element intersects the viewport.
// Use it to fade-and-lift sections into view on scroll. Respects
// `prefers-reduced-motion` (auto-visible). SSR-safe: on the server the
// observer isn't instantiated and elements render visible.
export default function useFadeInOnEnter({
  threshold = 0.12,
  rootMargin = "0px 0px -80px 0px",
} = {}) {
  const ref = React.useRef(null);
  const reducedMotion = useReducedMotion();
  // Server-render: assume visible so the HTML isn't blank until hydration.
  // After mount, reducedMotion tells us whether to skip the observer
  // entirely and stay visible.
  const [visible, setVisible] = React.useState(typeof window === "undefined");

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (visible) return undefined;
    if (reducedMotion) {
      setVisible(true);
      return undefined;
    }
    if (typeof IntersectionObserver === "undefined") {
      // Older browsers: skip the animation, just show.
      setVisible(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold, rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible, threshold, rootMargin, reducedMotion]);

  return [ref, visible];
}
